import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import FlagIcon from "@mui/icons-material/FlagOutlined";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import PsychologyIcon from "@mui/icons-material/Psychology";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ShieldIcon from "@mui/icons-material/Shield";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { alpha, useTheme } from "@mui/material";
import { GroundingRecord, LlmUsageSummary } from "../utils/backend-utils";
import { SessionCostPopover } from "./SessionCostPopover";

export type SessionPhase =
  | "clarification"
  | "drafting"
  | "review"
  | "starting"
  | "execution"
  | "completion"
  | "error";

interface SessionHeaderProps {
  goal?: string;
  sessionId?: string | null;
  phase: SessionPhase;
  /** Only used in 'execution' phase: completed steps / total steps. */
  progress?: { done: number; total: number };
  /** Full grounding record from the backend. When present and status!='ok',
   *  a slim warning strip is attached to the bottom of the header. */
  grounding?: GroundingRecord | null;
  llmUsage?: LlmUsageSummary | null;
  onScrollToMessage?: (sourceMessageTs: string) => void;
}

const PHASE_META: Record<SessionPhase, { label: string; Icon: typeof FlagIcon; paletteKey: "primary" | "info" | "warning" | "success" | "error" }> = {
  clarification: { label: "Clarification", Icon: SettingsSuggestIcon, paletteKey: "info" },
  drafting:      { label: "Building plan", Icon: PsychologyIcon,      paletteKey: "info" },
  review:        { label: "Review plan",    Icon: EditNoteIcon,         paletteKey: "warning" },
  starting:      { label: "Starting",       Icon: PlayArrowIcon,        paletteKey: "primary" },
  execution:     { label: "In progress",    Icon: PlayArrowIcon,        paletteKey: "primary" },
  completion:    { label: "Completed",      Icon: CheckCircleIcon,      paletteKey: "success" },
  error:         { label: "Error",          Icon: ReportProblemIcon,    paletteKey: "error" },
};

export function SessionHeader({ goal, sessionId, phase, progress, grounding, llmUsage, onScrollToMessage }: SessionHeaderProps) {
  const theme = useTheme();
  const meta = PHASE_META[phase];
  const phaseColor = theme.palette[meta.paletteKey].main;
  const { Icon } = meta;
  const [warningExpanded, setWarningExpanded] = useState(false);

  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.done / progress.total) * 100))
      : phase === "completion" ? 100
      : phase === "clarification" || phase === "drafting" ? 15
      : phase === "review" || phase === "starting" ? 40
      : 0;

  const showWarning = Boolean(grounding) && grounding!.status !== "ok";
  const showOkChip = Boolean(grounding) && grounding!.status === "ok";
  const warningIsNone = grounding?.status === "none";
  const warningColor = warningIsNone ? theme.palette.error.main : theme.palette.warning.main;
  const WarningIcon = warningIsNone ? ErrorOutlineIcon : WarningAmberIcon;

  const urlsOk = grounding?.urls_fetched || [];
  const urlsFailed = grounding?.urls_failed || [];

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 1.5, sm: 2.5 },
          py: 1,
          minHeight: 48,
        }}
      >
        <FlagIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />

        <Tooltip title={goal || ""} enterDelay={600} disableHoverListener={!goal}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontSize: "0.62rem",
                display: "block",
                lineHeight: 1,
                mb: 0.25,
              }}
            >
              Goal
            </Typography>
            <Typography
              noWrap
              sx={{
                color: theme.palette.text.primary,
                fontSize: "0.9rem",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {goal || "(no goal set)"}
            </Typography>
          </Box>
        </Tooltip>

        {showOkChip && (
          <Tooltip title={`Grounded in ${urlsOk.length} authoritative source${urlsOk.length === 1 ? "" : "s"}`}>
            <Chip
              size="small"
              icon={<ShieldIcon sx={{ fontSize: 14 }} />}
              label="Grounded"
              sx={{
                borderRadius: "6px",
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: theme.palette.success.dark,
                "& .MuiChip-icon": { color: theme.palette.success.dark, ml: "4px" },
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
        {showWarning && (
          <Tooltip title={warningIsNone ? "No authoritative docs loaded — click for details" : "Partial grounding — click for details"}>
            <Chip
              size="small"
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={warningIsNone ? "Ungrounded" : "Partial"}
              onClick={() => setWarningExpanded((v: boolean) => !v)}
              sx={{
                borderRadius: "6px",
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: alpha(warningColor, 0.15),
                color: warningColor,
                "& .MuiChip-icon": { color: warningColor, ml: "4px" },
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}

        <SessionCostPopover sessionId={sessionId} llmUsage={llmUsage} onScrollToMessage={onScrollToMessage} />

        <Chip
          size="small"
          icon={<Icon sx={{ fontSize: 14 }} />}
          label={
            phase === "execution" && progress && progress.total > 0
              ? `${meta.label} · ${progress.done}/${progress.total}`
              : meta.label
          }
          sx={{
            borderRadius: "6px",
            height: 24,
            fontSize: "0.72rem",
            fontWeight: 600,
            bgcolor: alpha(phaseColor, 0.12),
            color: phaseColor,
            "& .MuiChip-icon": { color: phaseColor, ml: "4px" },
            flexShrink: 0,
          }}
        />

        {sessionId && (
          <Tooltip title={`Session ${sessionId} · click to copy`}>
            <Typography
              component="span"
              onClick={() => {
                try { navigator.clipboard?.writeText(sessionId); } catch { /* no-op */ }
              }}
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "monospace",
                fontSize: "0.68rem",
                opacity: 0.55,
                cursor: "pointer",
                transition: "opacity 0.15s ease",
                flexShrink: 0,
                "&:hover": { opacity: 1 },
              }}
            >
              {sessionId.slice(0, 8)}…
            </Typography>
          </Tooltip>
        )}
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 3,
          bgcolor: "transparent",
          "& .MuiLinearProgress-bar": {
            bgcolor: phaseColor,
            transition: "width 0.4s ease",
          },
        }}
      />

      {showWarning && (
        <Box
          sx={{
            bgcolor: alpha(warningColor, 0.08),
            borderTop: `1px solid ${alpha(warningColor, 0.3)}`,
          }}
        >
          <Box
            onClick={() => setWarningExpanded((v: boolean) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: { xs: 1.5, sm: 2.5 },
              py: 0.75,
              cursor: "pointer",
              "&:hover": { bgcolor: alpha(warningColor, 0.12) },
            }}
          >
            <WarningIcon sx={{ fontSize: 16, color: warningColor }} />
            <Typography variant="caption" sx={{ flex: 1, color: warningColor, fontWeight: 600, fontSize: "0.75rem" }}>
              {warningIsNone
                ? "No authoritative docs loaded — this plan is LLM-only, review every step."
                : `Partial grounding — ${urlsOk.length} page(s) loaded, ${urlsFailed.length} failed. Some steps are LOW confidence.`}
            </Typography>
            <IconButton size="small" sx={{ color: warningColor, p: 0.25 }}>
              {warningExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
          <Collapse in={warningExpanded} unmountOnExit>
            <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 1.25, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {urlsOk.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, display: "block" }}>
                    Loaded:
                  </Typography>
                  {urlsOk.map((u) => (
                    <Typography key={u} variant="caption" component="a" href={u} target="_blank" rel="noopener noreferrer"
                      sx={{ display: "block", fontSize: "0.7rem", wordBreak: "break-all", color: theme.palette.text.secondary, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                      {u}
                    </Typography>
                  ))}
                </Box>
              )}
              {urlsFailed.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: warningColor, display: "block" }}>
                    Failed:
                  </Typography>
                  {urlsFailed.map((u) => (
                    <Typography key={u} variant="caption" sx={{ display: "block", fontSize: "0.7rem", wordBreak: "break-all", color: theme.palette.text.secondary }}>
                      {u}
                    </Typography>
                  ))}
                </Box>
              )}
              {grounding?.notes && (
                <Typography variant="caption" sx={{ fontStyle: "italic", color: theme.palette.text.secondary }}>
                  {grounding.notes}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
